// ── PRF PREMIUM RATE LOOKUP — the COMPLETE actuarial identity, or nothing ────
//
// A PRF base rate is identified by, at minimum:
//
//     crop year · state · county · grid · TYPE · PRACTICE · interval · coverage
//
// The PRF Grid map keyed its lookup on `interval|coverage` ALONE and took
// whichever row PostgREST happened to return last. Measured 2026-09-01 on the
// live table: **20,229 of 58,135 CY2026 (grid, interval, coverage) cells carry
// TWO different base rates**, so a customer-visible premium, producer cost and
// loss ratio were being decided by database row order on 35% of cells.
//
// It was worth $0.28/ac at worst on CY2026 — immaterial, which is why nobody
// saw it — and **$3.96/ac on CY2027**, because the 2027 rate spread is 14×
// wider. Pointing that page at 2027 without this fix is what makes it live.
//
// ── WHAT THE TWO CROP YEARS ACTUALLY LOOK LIKE, MEASURED ────────────────────
//
// They are NOT the same shape, and assuming they were is how a 2027 rollover
// would have gone wrong quietly:
//
//   CY2026   practice_code is NULL on every row. 3 types (007 grazing,
//            030 hay, 997), 58,135 rows each = one per (grid, county,
//            interval, coverage).
//   CY2027   practice_code is POPULATED and carries the practice band AND the
//            interval together. Grazing (007) has ONE band, 625–635 → JANFEB…
//            NOVDEC. **Hay (030) has SIX**: 425–435, 465–475, 485–495,
//            525–535, 565–575, 585–595, each covering all 11 intervals.
//            997 has one, 625–635.
//
// So for 2027 a single (grid, county, interval, coverage) has up to EIGHT rate
// rows behind it. Collapsing them is not a rounding choice; it is picking one
// grower's product and charging another for it.
//
// ── THE RULE: NARROW, OR REFUSE ─────────────────────────────────────────────
//
// `lookupPrfRate` NEVER picks. Given whatever the caller can specify it either
// returns exactly one rate, or reports `ambiguous` with the dimensions that
// still vary and the range they span. A caller that cannot narrow must show the
// range and say which choice would settle it — not a number.
//
// This is the same doctrine as the Field Data Compare unit key and the corn
// grain/silage type: a value that is not identified is not a value.

export const PRF_TYPE = { GRAZING: "007", HAY: "030", UNSPECIFIED: "997" };
export const PRF_TYPE_LABEL = {
  "007": "Grazing", "030": "Haying", "997": "Type not stated",
};

// The dimensions that identify a rate, in the order a UI would narrow them.
export const RATE_DIMENSIONS = ["county_code", "type_code", "practice_code"];

export const SELECT_COLUMNS =
  "grid_id,county_code,state_code,crop_year,type_code,practice_code,interval_code,coverage_level,base_rate";

const num = (v) => (v == null || v === "" ? null : Number(v));
const key = (r) => [r.county_code ?? "", r.type_code ?? "", r.practice_code ?? ""].join("|");

// Rows for one grid, narrowed by whatever the caller knows. Every filter is
// OPTIONAL: an unspecified dimension is not a wildcard match, it is a dimension
// that will show up in `varying` if it actually varies.
export function narrowRateRows(rows, { intervalCode, coverage, typeCode, practiceCode, countyCode } = {}) {
  const cov = num(coverage);
  return (rows || []).filter((r) => {
    if (intervalCode && String(r.interval_code) !== String(intervalCode)) return false;
    if (cov != null && Math.abs(num(r.coverage_level) - cov) > 1e-9) return false;
    if (typeCode && String(r.type_code) !== String(typeCode)) return false;
    if (practiceCode && String(r.practice_code) !== String(practiceCode)) return false;
    if (countyCode && String(r.county_code) !== String(countyCode)) return false;
    return true;
  });
}

// ⚠ RETURNS A RATE ONLY WHEN THE CANDIDATES AGREE. Distinct rates are reported,
// never resolved. `varying` names the dimensions a caller would have to pick to
// settle it, which is what lets a page say "choose haying or grazing" instead of
// printing a number nobody can reproduce.
export function lookupPrfRate(rows, sel = {}) {
  const cand = narrowRateRows(rows, sel);
  if (!cand.length) {
    return { rate: null, found: 0, ambiguous: false, reason: "no rate row for this selection",
             varying: [], lo: null, hi: null, candidates: [] };
  }
  const rates = [...new Set(cand.map((r) => num(r.base_rate)).filter((v) => v != null))];
  const varying = RATE_DIMENSIONS.filter((d) => new Set(cand.map((r) => r[d] ?? "")).size > 1);
  if (rates.length === 1) {
    // One rate. It does not matter that several identities share it — the
    // ANSWER is unambiguous even where the identity is not, and saying so is
    // more useful than refusing.
    return { rate: rates[0], found: cand.length, ambiguous: false, reason: null,
             varying, lo: rates[0], hi: rates[0],
             identityResolved: varying.length === 0,
             candidates: cand.map((r) => ({ k: key(r), rate: num(r.base_rate) })) };
  }
  return {
    rate: null, found: cand.length, ambiguous: true,
    reason: "rates differ across " + (varying.length ? varying.join(", ") : "identical identities — data defect"),
    varying, lo: Math.min(...rates), hi: Math.max(...rates),
    spread: Math.max(...rates) - Math.min(...rates),
    candidates: cand.map((r) => ({ k: key(r), rate: num(r.base_rate) })),
  };
}

// What a picker should offer, derived from the rows themselves rather than a
// hardcoded list — a type or practice RMA stops publishing disappears on its own.
export function rateOptions(rows, sel = {}) {
  const cand = narrowRateRows(rows, sel);
  const uniq = (f) => [...new Set(cand.map(f).filter((v) => v != null && v !== ""))].sort();
  return {
    counties: uniq((r) => r.county_code),
    types: uniq((r) => r.type_code),
    practices: uniq((r) => r.practice_code),
  };
}

// The practice codes that belong to one type, for one interval — this is what
// makes hay's six bands selectable without hardcoding 425/465/485/525/565/585.
export function practicesFor(rows, { typeCode, intervalCode } = {}) {
  return [...new Set(narrowRateRows(rows, { typeCode, intervalCode })
    .map((r) => r.practice_code).filter(Boolean))].sort();
}

// A one-line, checkable description of what the number on screen refers to.
// Printed beside every premium the map shows.
export function rateProvenance(sel = {}, res = {}) {
  const bits = [];
  if (sel.countyCode) bits.push("county " + sel.countyCode);
  if (sel.typeCode) bits.push(PRF_TYPE_LABEL[sel.typeCode] || ("type " + sel.typeCode));
  if (sel.practiceCode) bits.push("practice " + sel.practiceCode);
  if (sel.intervalCode) bits.push(sel.intervalCode);
  if (sel.coverage != null) bits.push(Math.round(num(sel.coverage) * 100) + "%");
  if (!bits.length) return "no selection";
  const tail = res && res.ambiguous
    ? " — AMBIGUOUS across " + (res.varying || []).join(", ")
    : (res && res.identityResolved === false ? " (one rate across " + (res.varying || []).join(", ") + ")" : "");
  return bits.join(" · ") + tail;
}
